import { useState } from 'react';
import {
  PhoneOutgoing,
  PhoneOff,
  ChevronDown,
  CircleAlert,
  CircleCheck,
  Sparkles,
  ArrowRightLeft,
  ShieldAlert,
  Wrench,
  ListChecks,
} from 'lucide-react';
import { OUTBOUND_CALL_OUTCOME } from '@vagusx/shared';
import type {
  CallEvaluationCallbackRecommendation,
  CallEvaluationCategory,
  CallEvaluationResponse,
} from '@vagusx/clients';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { useInteractionEvaluation } from '@/hooks/use-interactions';
import { useIsAdmin } from '@/hooks/use-permissions';
import { statusTone } from './call-status-tone';
import { CallStatusPill } from './CallStatusPill';

interface CallEvaluationSectionProps {
  interactionId: string;
}

/** Human-readable labels for the LLM's own callback verdict. */
const CALLBACK_RECOMMENDATION_LABELS: Record<CallEvaluationCallbackRecommendation, string> = {
  recommend: 'Recommend callback',
  patient_declined: 'Patient declined',
  wrong_number: 'Wrong number',
  booked: 'Booked',
};

/** The practice-facing name of each category. Ordered most- to least-critical. */
const CATEGORY_LABELS: Record<CallEvaluationCategory, string> = {
  safety: 'Safety',
  accuracy: 'Accuracy',
  playbook: 'Followed your playbook',
  experience: 'Patient experience',
};

const CATEGORY_ORDER: CallEvaluationCategory[] = ['safety', 'accuracy', 'playbook', 'experience'];

/**
 * Renders the post-call evaluation for a single call. Used by the Activity
 * page (`ActivityPage.tsx`) and the call-details sheet.
 *
 * ── Calm by default, loud by exception ───────────────────────────────────
 * A call that went well should be almost nothing to READ. Every text element —
 * the office note, a category's explanation, the whole internal block —
 * appears ONLY when it has something to say. That is why so much of this
 * component is conditional: a good call must not get busier just because more
 * fields exist.
 *
 * ── Colour carries the signal ────────────────────────────────────────────
 * The category icons are deliberately coloured — green tick, amber
 * exclamation — so the four areas can be triaged at a glance without reading a
 * word. This SUPERSEDES the original "four quiet check marks in muted grey"
 * treatment, which made a clean call and a problem call look identical until
 * you read them. Do not revert it to muted as a tidy-up.
 *
 * What has NOT changed is the client REGISTER: the copy stays plain business
 * language, never a report card on the AI, and the status colour comes from
 * `statusTone` so the pill, the office-note box and these icons cannot
 * disagree about what amber means.
 *
 * ── Why the two tiers are separate components ────────────────────────────
 * `LastCallSection` (the task overview page) renders the SAME evaluation as a
 * collapsible card: tier 1 always visible, tier 2 behind an expand. It composes
 * {@link EvaluationClientSummary} and {@link EvaluationDetail} directly rather
 * than re-implementing either — one office-note box, one set of category rows,
 * so the two surfaces cannot drift.
 *
 * Both tiers return FRAGMENTS, not wrapper divs: their children are direct
 * children of a `space-y-*` container here and in `LastCallSection`, and an
 * intermediate div would collapse that spacing into one gap.
 */
export function CallEvaluationSection({
  interactionId,
}: CallEvaluationSectionProps): React.ReactElement | null {
  const { data: evaluation } = useInteractionEvaluation(interactionId);
  const isAdmin = useIsAdmin();

  if (!evaluation) return null;

  const showInternal = isAdmin && hasInternalContent(evaluation);
  if (!hasClientContent(evaluation) && !showInternal) return null;

  return (
    // `shrink-0`: a no-op outside a flex container, but load-bearing inside
    // InteractionDetailSheet's flex-column body, where the transcript below is
    // `flex-1` and cannot absorb overflow. Without it this card gets squashed
    // instead of pushing the sheet body into its own scroll.
    <div className="bg-card border rounded-lg p-4 space-y-4 shrink-0">
      <EvaluationClientSummary evaluation={evaluation} />
      <EvaluationDetail evaluation={evaluation} />
    </div>
  );
}

/**
 * Tier 1 · what happened — the practice-facing block, and everything BEFORE the
 * category rows. Headline, outcome, the office note, the callback badge.
 *
 * This is the whole of what a front-office manager needs to decide whether
 * anyone has to act, which is why `LastCallSection` shows it collapsed.
 *
 * `showStatusPill` defaults to true — the pill belongs beside the headline
 * here. `LastCallSection` passes false because it promotes the same pill to a
 * signal row above the headline, where it is the first thing on the card;
 * without the opt-out the call would show two identical pills.
 */
export function EvaluationClientSummary({
  evaluation,
  showStatusPill = true,
}: {
  evaluation: CallEvaluationResponse;
  showStatusPill?: boolean;
}): React.ReactElement {
  const headline = evaluation.headline?.trim();
  const officeNote = evaluation.officeNote?.trim();
  const outcomeLabel = evaluation.outcomeLabel?.trim();
  const status = evaluation.status;
  const tone = statusTone(status);

  return (
    <>
      <div className="flex flex-wrap items-start justify-between gap-2">
        {headline && <p className="text-sm text-foreground leading-relaxed flex-1">{headline}</p>}
        {showStatusPill && status && <CallStatusPill status={status} />}
      </div>

      {outcomeLabel && <p className="text-sm font-medium text-foreground">{outcomeLabel}</p>}

      {/* The office note carries either an action the practice owes or the one
          notable thing Iris did.

          COLOUR comes from `status`, so the box matches the pill — it used to
          branch on `recommendCallback`, which meant a `needs_you` call and a
          `follow_up` call rendered identically. The ICON still branches on
          `recommendCallback`, because "is there an action" is orthogonal to
          "how bad was it": a red call can still be a highlight, and a green one
          can still owe a callback. */}
      {officeNote && (
        <div
          className={cn('flex gap-2 items-start rounded-md border p-3', tone?.box ?? 'bg-muted/50')}
        >
          {evaluation.recommendCallback ? (
            <ArrowRightLeft
              className={cn('h-4 w-4 mt-0.5 shrink-0', tone?.boxIcon ?? 'text-muted-foreground')}
            />
          ) : (
            <Sparkles
              className={cn('h-4 w-4 mt-0.5 shrink-0', tone?.boxIcon ?? 'text-muted-foreground')}
            />
          )}
          <p className="text-sm text-foreground leading-relaxed">{officeNote}</p>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {evaluation.recommendCallback ? (
          <Badge variant="default" className="gap-1">
            <PhoneOutgoing className="h-3 w-3" />
            Callback recommended
          </Badge>
        ) : (
          <Badge variant="secondary" className="gap-1">
            <PhoneOff className="h-3 w-3" />
            No callback needed
          </Badge>
        )}
      </div>
    </>
  );
}

/**
 * Tier 2 · how well it was handled — the four category rows, plus the
 * system-admin-only internal block, which is collapsed behind its own
 * disclosure so the scores don't restate the rows above them.
 *
 * The INTERNAL block (scores, findings, gates, grade) is system-admin only. The
 * API already strips those fields for everyone else — the `useIsAdmin()` check
 * lives HERE rather than at the call sites so it travels with the block it
 * guards; it is defense-in-depth, never the enforcement point.
 *
 * `null` when there is nothing on either half — the caller can use that to
 * decide whether an expand control is worth offering at all.
 */
export function EvaluationDetail({
  evaluation,
  showBookingClaimAlarm = true,
}: {
  evaluation: CallEvaluationResponse;
  /**
   * Whether the booking-claim alarm may ride the disclosure trigger. Defaults
   * to true — on the call sheet the trigger is the highest place it can go.
   * `LastCallSection` passes false because it promotes the same badge to its
   * always-visible signal row; without the opt-out an expanded card would show
   * two identical alarms. Exactly the `showStatusPill` arrangement above, for
   * exactly the same reason.
   */
  showBookingClaimAlarm?: boolean;
}): React.ReactElement | null {
  const isAdmin = useIsAdmin();
  const [internalOpen, setInternalOpen] = useState(false);
  const categories = evaluation.categories ?? null;
  const showInternal = isAdmin && hasInternalContent(evaluation);

  if (!categories && !showInternal) return null;

  return (
    <>
      {categories && (
        <div className="border-t pt-3 space-y-2">
          {CATEGORY_ORDER.map((name) => {
            const category = categories[name];
            if (!category) return null;
            const clear = category.checkmark === 'clear';
            return (
              <div key={name} className="flex gap-2 items-start">
                {clear ? (
                  <CircleCheck className="h-4 w-4 mt-0.5 text-success shrink-0" />
                ) : (
                  <CircleAlert className="h-4 w-4 mt-0.5 text-warning shrink-0" />
                )}
                <p className="text-sm leading-relaxed">
                  <span className="font-medium text-foreground">{CATEGORY_LABELS[name]}</span>
                  {category.clientNote && (
                    <span className="text-muted-foreground"> — {category.clientNote}</span>
                  )}
                </p>
              </div>
            );
          })}
        </div>
      )}

      {/* Collapsed by default. The internal block re-states the same four
          categories the client rows above already cover — plain-language note
          up there, numeric score and engineering finding down here — so for an
          admin the card read as the evaluation twice over. Behind a disclosure
          the two tiers stop competing, and the one internal signal worth
          triaging on (the grade) is promoted onto the trigger so collapsing it
          costs no information at a glance.

          Local state, not a prop: whether an admin wants the scores open is a
          per-viewer reading preference, and neither caller has an opinion about
          it. `LastCallSection` nests this inside its own expand — two levels,
          but each level is a real step down in audience. */}
      {showInternal && (
        <Collapsible open={internalOpen} onOpenChange={setInternalOpen} className="border-t pt-3">
          <CollapsibleTrigger className="flex w-full items-center gap-2 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground">
            <ChevronDown
              className={cn('h-3.5 w-3.5 transition-transform', internalOpen && 'rotate-180')}
            />
            Internal scoring
            {!internalOpen && <GradeBadge evaluation={evaluation} />}
            {/* An unbacked booking claim rides the trigger for the same reason
                the grade does — this disclosure hides detail, never an alarm.
                On the call sheet that is as high as it goes. It is NOT enough on
                `LastCallSection`, which renders all of `EvaluationDetail` inside
                its own expand: there the badge is promoted to the signal row and
                this copy is suppressed. */}
            {!internalOpen && showBookingClaimAlarm && hasBookingClaimMismatch(evaluation) && (
              <BookingClaimMismatchBadge compact />
            )}
          </CollapsibleTrigger>
          <CollapsibleContent>
            <InternalBlock evaluation={evaluation} />
          </CollapsibleContent>
        </Collapsible>
      )}
    </>
  );
}

/**
 * The overall grade, with the score to two decimals when there is one.
 *
 * Rendered in two places — on the collapsed disclosure trigger and inside the
 * block itself — so it lives here rather than being written twice and drifting.
 */
function GradeBadge({ evaluation }: { evaluation: CallEvaluationResponse }): React.ReactElement {
  if (!evaluation.grade) return <Badge variant="secondary">Ungraded</Badge>;
  return (
    <Badge variant={evaluation.grade === 'F' ? 'destructive' : 'secondary'}>
      Grade {evaluation.grade}
      {evaluation.overallScore !== null && evaluation.overallScore !== undefined
        ? ` · ${evaluation.overallScore.toFixed(2)}`
        : ''}
    </Badge>
  );
}

/**
 * Did the judge route this call as a booking that the appointment record does
 * not support?
 *
 * `llmCallbackRecommendation` is the evaluator's OWN verdict on what the call
 * needs next, persisted raw beside the codebase's deterministic decision so the
 * two can be compared (`decision.ts`). `booked` means the judge believed an
 * appointment was committed. When the disposition says otherwise, the model is
 * asserting a booking that does not exist — the confabulation failure mode the
 * whole evaluator exists to catch — and the raw chip rendered that identically
 * to a benign agreement.
 *
 * ── Why this is NOT the G-CONFAB gate ────────────────────────────────────
 * G-CONFAB is the stronger, narrower signal: the agent TOLD THE PATIENT they
 * were booked and no appointment was created. It passes when
 * `agentClaimedBooking` is false and on a `fallback` evaluation. This predicate
 * catches the adjacent case the gate is silent on — the agent never said it
 * outright, but the judge still read the call as ending in a booking. Two nets,
 * different holes; do not collapse them into one.
 *
 * ── Why it does NOT read the disposition ─────────────────────────────────
 * `outcome.ts` states the rule: `booked = outcomeCategory === SCHEDULED ||
 * bookedOnThisCall` — "a booking is a booking whether or not a disposition
 * recorded it". The two really do disagree: `deriveOutboundOutcome` records
 * that reading the booking field directly wrote `engaged_no_booking` for 6 of 6
 * production v2 calls that had committed a real appointment (ADR-188), and
 * older inbound rows carry no disposition at all. An earlier cut of this predicate
 * asked `outcomeCategory`, and would have accused the judge of confabulating an
 * appointment that exists — beside an `outcomeLabel` already reading
 * "Appointment booked — …", because `formatOutcome` consults the record.
 *
 * `bookedOnThisCall` IS that record: resolved per-read from
 * `scheduled_appointments.interaction_id`, the SAME fact G-CONFAB judges on, so
 * the badge and the gate cannot reach opposite conclusions about one call and
 * this is exactly as reliable as that gate. It needs no fallback — being
 * derived rather than stored, it answers for historical rows too — and a later
 * cancellation does not flip it, since the lookup ignores appointment `status`:
 * a booking cancelled next week still happened on the call.
 *
 * ── Why the disposition must ALSO deny the booking ───────────────────────
 * The record is the better signal but not an infallible one: `interaction_id`
 * is stamped by the booking path, so a commit that reached the EHR without
 * writing a local row reads `false` here while `deriveOutboundOutcome` — which
 * asks the fact ledger — writes `scheduled`. Firing on the record alone put a
 * red "no appointment recorded" beside the `BookingPill`'s green "Booked" on
 * that call, since the pill honours the same disposition, and left the operator
 * with two answers and no way to choose.
 *
 * So the alarm requires BOTH to deny it. That is the right way round for an
 * accusatory badge: the cost of demanding the stronger evidence is a
 * confabulation missed on a call whose ledger wrongly says `scheduled`, and
 * G-CONFAB — built for exactly that — still catches it. The cost of the
 * opposite default is a card that contradicts itself, which teaches operators
 * to disbelieve the badge.
 *
 * A call with NO disposition still fires: `null !== 'scheduled'`, which is what
 * keeps inbound covered.
 */
export function hasBookingClaimMismatch(evaluation: CallEvaluationResponse): boolean {
  return (
    evaluation.llmCallbackRecommendation === 'booked' &&
    !evaluation.bookedOnThisCall &&
    evaluation.outcomeCategory !== OUTBOUND_CALL_OUTCOME.SCHEDULED
  );
}

/**
 * The mismatch alarm. `compact` where it sits beside other pills — the
 * collapsed disclosure trigger, or `LastCallSection`'s signal row, where
 * "AI claimed booked" lands next to a `BookingPill` reading "No appointment
 * booked" and the contradiction reads itself. Full text inside the block, where
 * it replaces the neutral `AI: Booked` chip it is contradicting.
 *
 * Exported for the surfaces that promote it above the disclosure. It needs no
 * RBAC gate of its own: `hasBookingClaimMismatch` reads
 * `llmCallbackRecommendation`, which the API omits entirely for non-admins, so
 * the badge cannot render for a viewer who was never sent the verdict.
 */
export function BookingClaimMismatchBadge({
  compact = false,
}: {
  compact?: boolean;
}): React.ReactElement {
  return (
    <Badge variant="destructive" className="gap-1">
      <CircleAlert className="h-3 w-3" />
      {compact ? 'AI claimed booked' : 'AI claimed booked — no appointment recorded'}
    </Badge>
  );
}

/**
 * The four category checks, counted — for a collapsed control that has to say
 * what is behind it without restating it.
 *
 * The point is that a disclosure hiding a PROBLEM must not read as neutral.
 * "4 checks clear" and "1 needs a look" are the same control in two states,
 * and the second one is why the count is here rather than a static label.
 *
 * Returns null when the evaluation carries no categories at all (a v1 row, or
 * an evaluation that failed to produce them), so the caller can decline to
 * render the control rather than offering an expand onto nothing.
 */
export function summarizeCategories(
  evaluation: CallEvaluationResponse
): { clear: number; attention: number; label: string } | null {
  const categories = evaluation.categories;
  if (!categories) return null;

  const present = CATEGORY_ORDER.map((name) => categories[name]).filter(
    (category) => category != null
  );
  if (present.length === 0) return null;

  const attention = present.filter((category) => category.checkmark !== 'clear').length;
  const clear = present.length - attention;

  // The exception leads when there is one — that is the fact worth expanding
  // for. `1 to review` rather than `1 failed`: this side of the RBAC line the
  // register stays plain business language, never a report card (ADR-154).
  const label = attention > 0 ? `${attention} to review` : `${clear} checks clear`;
  return { clear, attention, label };
}

/**
 * Does {@link EvaluationClientSummary} have anything to say?
 *
 * Exported because the callback badge at the foot of that fragment is
 * UNCONDITIONAL — it renders "No callback needed" whether or not the row
 * carries a headline, an outcome or an office note. A surface that composes
 * the summary directly, rather than through {@link CallEvaluationSection},
 * must ask this first or a fallback evaluation (every client field
 * `nullable().default(null)`) puts a lone chip on screen explaining nothing.
 */
export function hasClientContent(evaluation: CallEvaluationResponse): boolean {
  return (
    !!evaluation.headline?.trim() ||
    !!evaluation.officeNote?.trim() ||
    !!evaluation.outcomeLabel?.trim() ||
    !!evaluation.categories
  );
}

/**
 * Will {@link EvaluationDetail} render anything for THIS viewer?
 *
 * Mirrors that component's own early return — `!categories && !showInternal`
 * — so a caller deciding whether to draw a disclosure around it cannot
 * disagree with what the disclosure would contain.
 *
 * A HOOK rather than a predicate because the answer depends on the viewer:
 * the internal half is system-admin only. Keeping `useIsAdmin` in here rather
 * than at the call sites is the same rule `EvaluationDetail` follows — the
 * RBAC check travels with the block it guards, and callers cannot forget it.
 *
 * Categories alone are NOT enough to answer this: `hasInternalContent` reads
 * `categoryScores`, `grade`, `gateResults` and `improvements`, none of which
 * imply the client-facing `categories`. Gating on categories alone hid the
 * grade, the safety-gate verdicts and the improvement list from admins on
 * exactly the degraded rows worth inspecting.
 */
export function useHasEvaluationDetail(
  evaluation: CallEvaluationResponse | null | undefined
): boolean {
  const isAdmin = useIsAdmin();
  if (!evaluation) return false;
  return !!evaluation.categories || (isAdmin && hasInternalContent(evaluation));
}

/** Does this row carry anything a staff viewer would want to see? */
function hasInternalContent(evaluation: CallEvaluationResponse): boolean {
  return (
    !!evaluation.categoryScores ||
    !!evaluation.grade ||
    (evaluation.gateResults?.length ?? 0) > 0 ||
    (evaluation.improvements?.length ?? 0) > 0
  );
}

/**
 * Everything behind the RBAC line: the raw scores, the engineering findings,
 * the gate verdicts, the computed playbook steps and the improvement list.
 *
 * A v1 row has no `categoryScores`; it renders its retained legacy notes
 * instead of an empty shell, so pre-restructure history stays readable rather
 * than looking like an evaluation that produced nothing.
 */
function InternalBlock({ evaluation }: { evaluation: CallEvaluationResponse }): React.ReactElement {
  const gateResults = evaluation.gateResults ?? [];
  const failedGates = gateResults.filter((g) => !g.passed);
  const improvements = evaluation.improvements ?? [];
  const steps = evaluation.playbookSteps;

  // No `border-t` here: the collapsible wrapper in `EvaluationDetail` draws the
  // rule above the trigger, so repeating it would put a second line inside the
  // opened block.
  return (
    <div className="pt-3 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline">Internal</Badge>
        <GradeBadge evaluation={evaluation} />
        {/* The judge's raw routing verdict. It renders as a neutral chip when
            it agrees with the appointment record and as an alarm when it does
            not — see `hasBookingClaimMismatch`. Rendering both cases the same
            way was the whole problem: the single most damaging thing this
            evaluation can tell you looked exactly like the most ordinary. */}
        {evaluation.llmCallbackRecommendation &&
          (hasBookingClaimMismatch(evaluation) ? (
            <BookingClaimMismatchBadge />
          ) : (
            <Badge variant="outline" className="gap-1">
              AI: {CALLBACK_RECOMMENDATION_LABELS[evaluation.llmCallbackRecommendation]}
            </Badge>
          ))}
      </div>

      {/* Gates sit under Safety conceptually; they are rendered here because
          they are proven from the timeline, not scored. */}
      {failedGates.length > 0 ? (
        <div>
          <div className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground mb-1">
            <ShieldAlert className="h-3.5 w-3.5" />
            Safety gates
          </div>
          <ul className="space-y-1">
            {failedGates.map((gate) => (
              <li key={gate.gate} className="text-sm text-muted-foreground leading-relaxed">
                <span className="font-medium text-foreground">{gate.gate}</span>
                {gate.detail ? ` — ${gate.detail}` : ''}
                {gate.evidenceSeq !== null ? ` (turn ${gate.evidenceSeq})` : ''}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        gateResults.length > 0 && (
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <CircleCheck className="h-3.5 w-3.5" />
            All safety gates passed
          </div>
        )
      )}

      {/* Per-category scores + engineering findings. */}
      {evaluation.categoryScores && (
        <div className="space-y-2">
          {CATEGORY_ORDER.map((name) => {
            const category = evaluation.categoryScores?.[name];
            if (!category) return null;
            return (
              <div key={name}>
                <div className="text-sm">
                  <span className="font-medium text-foreground">{CATEGORY_LABELS[name]}</span>
                  <span className="text-muted-foreground"> · {category.score}/5</span>
                </div>
                {category.findings.length > 0 && (
                  <ul className="mt-0.5 space-y-0.5">
                    {category.findings.map((finding) => (
                      <li key={finding} className="text-sm text-muted-foreground leading-relaxed">
                        {finding}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* A missed step is a fact, not a verdict — the evaluator explains it in
          the playbook findings above. Shown so the two can be read together. */}
      {steps && steps.missed.length > 0 && (
        <div className="flex items-start gap-1.5 text-sm text-muted-foreground">
          <ListChecks className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>Steps not run: {steps.missed.join(', ')}</span>
        </div>
      )}

      {improvements.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground mb-1">
            <Wrench className="h-3.5 w-3.5" />
            Improvements
          </div>
          <ul className="space-y-1">
            {improvements.map((item) => (
              <li key={item} className="text-sm text-foreground leading-relaxed">
                {item}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
